// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skyhold — bootstrap and the screen machine. Owns which screen is up, the room's Net (created ONCE
// per session and never left for a rematch), the tap machine that turns three taps into one packed
// ply, and the setInterval that drives every clock. Rules live in rules.ts, the search in bot.ts,
// the round in duel.ts, the board in render.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';
import { newSeed } from '@ben-gy/game-engine/rng';

import { escapeHtml, fmtTime } from './dom';
import { MODE_IDS, modeOf } from './modes';
import {
  NONE,
  PH_DRAFT,
  PH_PLACE,
  PH_PLAY,
  P_MASON,
  buildersOf,
  cellName,
  isOver,
  packDraft,
  plyA,
  plyAux,
  plyBuild,
  plyBuilder,
  plyPower,
  powerOf,
  seatOfBuilder,
  type PlyEvent,
} from './rules';
import { DEFAULT_TIER, chooseAny, optsForTier, tierOf } from './bot';
import { Duel, seatForRound, type WireMsg } from './duel';
import { BoardView, emptyHints, renderIso, type LastMove, type ViewHints } from './render';
import { runCountdown } from './countdown';
import { analyse, type Analysis, type Verdict } from './analysis';
import { ATTRIB, aboutHtml, helpHtml, menuHtml, resultsHtml, verdictHtml } from './ui';
import { SEAT_COLOUR, SEAT_NAME, PALETTE } from './palette';

const SLUG = 'skyhold';

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

const playerName = resolveName(store, () => `Builder ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;
let myTier = tierOf(store.get<string>('tier', DEFAULT_TIER)).id;

const recordKey = (id: string): string => `rec_${id}`;
function recordFor(id: string): [number, number] {
  const v = store.get<number[]>(recordKey(id), [0, 0]) ?? [0, 0];
  return [v[0] | 0, v[1] | 0];
}
function noteResult(id: string, won: boolean): void {
  const r = recordFor(id);
  store.set(recordKey(id), won ? [r[0] + 1, r[1]] : [r[0], r[1] + 1]);
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let duel: Duel | null = null;
let roster: RoundPlayer[] = [];
let tally: [number, number] = [0, 0];
let roomCode = '';
let roundNo = 0;
let soloRound = 0;

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  const armedAt = Date.now();
  wrap.addEventListener('pointerdown', (e) => {
    // A sheet opened from a pointerup would otherwise be closed by that gesture's trailing click.
    if (e.target === wrap && Date.now() - armedAt > 350) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, tierId: myTier, record: recordFor(myModeChoice), muted: sfx.muted() });

  content.querySelectorAll<HTMLElement>('.chip[data-mode]').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      ping('select');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('.chip[data-tier]').forEach((c) =>
    c.addEventListener('click', () => {
      myTier = tierOf(c.dataset.tier).id;
      store.set('tier', myTier);
      ping('select');
      showMenu();
    }),
  );
  content.querySelector('#playSolo')!.addEventListener('click', () => startSolo());
  content.querySelector('#playFriends')!.addEventListener('click', () => showRoomEntry());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

function ping(cue: string, pitch?: number): void {
  try {
    sfx.play(cue, pitch === undefined ? undefined : { pitch });
  } catch {
    /* audio is best-effort and must never break a turn */
  }
}

// ── the tap machine ─────────────────────────────────────────────────────────────
// A turn is one packed ply, and the player builds it up one field at a time. Rather than a bespoke
// flow per power, each power declares the ORDER of the fields it needs and the machine filters the
// legal ply list down as taps arrive — so Mason's second block and Courier's second step are the
// same three lines of code as an ordinary move, and an illegal combination is unreachable by
// construction rather than by validation.
type Field = 'a' | 'build' | 'aux';
const FIELDS: Record<number, Field[]> = {
  0: ['a', 'build'],
  1: ['a', 'build', 'aux'], // mason: destination, block, second block
  2: ['a', 'build'], // warden: destination, dome
  3: ['a', 'build'], // gale: destination (onto the rival), block
  4: ['aux', 'a', 'build'], // courier: first step, second step, block
  5: ['a', 'build'], // scout: destination, block beside the other builder
};
const readField = (ply: number, f: Field): number => (f === 'a' ? plyA(ply) : f === 'build' ? plyBuild(ply) : plyAux(ply));

/**
 * Which marker a field asks for. It is NOT the same question as which field it is: Mason's `aux` is
 * a second BLOCK while Courier's `aux` is a first STEP, and drawing a step marker over a square the
 * player is about to build on is a lie about what the tap does.
 */
const markerFor = (power: number, f: Field): 'move' | 'build' =>
  f === 'build' || (power === P_MASON && f === 'aux') ? 'build' : 'move';

interface TurnUi {
  sel: number | null;
  armed: number;
  taps: number[];
}

function freshTurn(): TurnUi {
  return { sel: null, armed: 0, taps: [] };
}

function candidates(legal: number[], ui: TurnUi): number[] {
  if (ui.sel === null) return [];
  const fields = FIELDS[ui.armed] ?? FIELDS[0];
  return legal.filter((p) => {
    if (plyBuilder(p) !== ui.sel) return false;
    if (plyPower(p) !== ui.armed) return false;
    for (let i = 0; i < ui.taps.length; i++) {
      if (readField(p, fields[i]) !== ui.taps[i]) return false;
    }
    return true;
  });
}

/** Advance past any field the current candidates leave no choice about (a win has no build). */
function settle(legal: number[], ui: TurnUi): number[] {
  const fields = FIELDS[ui.armed] ?? FIELDS[0];
  for (let guard = 0; guard < 4; guard++) {
    if (ui.taps.length >= fields.length) break;
    const cands = candidates(legal, ui);
    const vals = new Set(cands.map((p) => readField(p, fields[ui.taps.length])));
    if (vals.size === 1 && vals.has(NONE)) {
      ui.taps.push(NONE);
      continue;
    }
    break;
  }
  return candidates(legal, ui);
}

// ── the game screen ─────────────────────────────────────────────────────────────
interface Screen {
  destroy(): void;
}

let activeDuel: Duel | null = null;
let activeSubmit: ((ply: number) => boolean) | null = null;

function buildGameScreen(d: Duel, onQuit: () => void, onOver: () => void): Screen {
  const mode = d.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-turn" id="hudTurn"></span>
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <button class="iconbtn" id="pause" title="Pause">❚❚</button>
      </div>
      <div class="clockbar" id="clockbar" hidden><i id="clockfill"></i></div>
      <div class="steps" id="steps"></div>
      <div class="board-host" id="boardHost"></div>
      <p class="prompt" id="prompt"></p>
      <div class="tilebar" id="tilebar"></div>
      <div class="turn-actions" id="actions"></div>
      <aside class="side" id="side"><h3>Moves</h3><ul class="movelist" id="movelist"></ul></aside>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const hudTurn = content.querySelector<HTMLElement>('#hudTurn')!;
  const stepsEl = content.querySelector<HTMLElement>('#steps')!;
  const promptEl = content.querySelector<HTMLElement>('#prompt')!;
  const tilebar = content.querySelector<HTMLElement>('#tilebar')!;
  const actions = content.querySelector<HTMLElement>('#actions')!;
  const clockbar = content.querySelector<HTMLElement>('#clockbar')!;
  const clockfill = content.querySelector<HTMLElement>('#clockfill')!;
  const movelist = content.querySelector<HTMLElement>('#movelist')!;

  const view = new BoardView(boardHost, d.state, (cell) => onCell(cell), reducedMotion);
  let ui = freshTurn();
  let live = false;
  let destroyed = false;
  let last: LastMove | null = null;
  let botTimer: ReturnType<typeof setTimeout> | null = null;

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    paint();
    maybeBot();
  });

  function legalNow(): number[] {
    return live ? d.legal() : [];
  }

  function hints(): ViewHints {
    const h = emptyHints();
    h.last = last;
    h.mySeat = d.mySeat;
    h.interactive = live && d.isMyTurn;
    h.trapped = d.trappedCells();
    h.winCell = d.state.reason === 'climb' && d.state.winner >= 0 ? d.state.pos[buildersOf(d.state, d.state.winner).find((b) => d.state.lev[d.state.pos[b]] >= 3) ?? 0] : null;
    if (!live || !d.isMyTurn) return h;

    const legal = legalNow();
    if (d.state.phase === PH_PLACE) {
      // Every empty square is legal here, so ringing all of them is noise that hides the terrain
      // the player is supposed to be reading. The prompt carries it instead.
      return h;
    }
    if (d.state.phase !== PH_PLAY) return h;

    h.selected = ui.sel;
    if (ui.sel === null) return h;
    const fields = FIELDS[ui.armed] ?? FIELDS[0];
    const cands = candidates(legal, ui);
    const next = fields[ui.taps.length];
    if (next) {
      const kind = markerFor(ui.armed, next);
      for (const p of cands) {
        const v = readField(p, next);
        if (v === NONE) continue;
        if (kind === 'build') h.buildTargets.add(v);
        else h.moveTargets.add(v);
      }
    }
    const b = buildersOf(d.state, d.state.turn)[ui.sel];
    h.pendingFrom = d.state.pos[b];
    // Where the builder is standing right now, mid-turn.
    let at: number | null = null;
    for (let i = 0; i < ui.taps.length; i++) {
      if (fields[i] !== 'build' && ui.taps[i] !== NONE) at = ui.taps[i];
    }
    h.pendingAt = at;
    return h;
  }

  function paint(): void {
    if (destroyed) return;
    const s = d.state;
    view.paint(s, hints());

    const seat = s.turn;
    const yours = d.mySeat === seat;
    hudTurn.innerHTML =
      d.fault !== null
        ? '<span class="fault-note">Round stopped</span>'
        : isOver(s)
          ? `<span>${escapeHtml(s.winner >= 0 ? `${d.names[s.winner]} wins` : 'Round over')}</span>`
          : `<span class="rtok p${seat + 1}"></span><span>${escapeHtml(d.mySeat === null ? `${d.names[seat]} to play` : yours ? 'Your turn' : `${d.names[seat]} is thinking…`)}</span>`;

    if (s.phase === PH_PLAY && !isOver(s)) {
      const moved = ui.taps.length > 0 && (FIELDS[ui.armed] ?? FIELDS[0]).slice(0, ui.taps.length).some((f) => f !== 'build');
      const built = (FIELDS[ui.armed] ?? FIELDS[0]).slice(0, ui.taps.length).includes('build');
      stepsEl.innerHTML = `<b class="${built ? 'done' : moved ? 'done' : yours ? 'on' : ''}">① Move</b><span>→</span><b class="${built ? 'done' : moved && yours ? 'on' : ''}">② Build</b>`;
    } else {
      stepsEl.innerHTML = '';
    }

    // The draft list needs the vertical room the board is using, and there is nothing to tap on the
    // board yet — so shrink it rather than hide it (the terrain is what a Scout or Gale pick is
    // actually about).
    content.querySelector('.game')?.classList.toggle('drafting', s.phase === PH_DRAFT);
    promptEl.textContent = promptText();
    renderTiles();
    renderActions();
    renderClock();
    renderMoves();
  }

  function promptText(): string {
    const s = d.state;
    if (d.fault === 'build') return 'Your opponent is on a different build of Skyhold — reload to play again.';
    if (d.fault === 'desync') return 'The two boards stopped agreeing, so the round was stopped.';
    if (d.missing !== null) return `${d.names[d.missing]} dropped out — holding the round for ${Math.ceil(d.holdLeft / 1000)}s…`;
    if (isOver(s)) return '';
    if (!live) return '';
    if (d.mySeat === null) return 'Watching — you are in the next round.';
    if (!d.isMyTurn) return `Waiting for ${d.names[s.turn]}…`;
    if (s.phase === PH_DRAFT) return 'Choose a power tile.';
    if (s.phase === PH_PLACE) return 'Tap an empty square to place a builder.';
    if (ui.sel === null) return 'Tap one of your builders.';
    const fields = FIELDS[ui.armed] ?? FIELDS[0];
    const next = fields[ui.taps.length];
    if (next === 'build') return ui.armed === 2 ? 'Tap a square to cap with a dome.' : 'Now raise a block beside your builder.';
    if (next === 'aux' && ui.armed === 1) return 'Raise a second block.';
    return 'Move to a highlighted square.';
  }

  function renderTiles(): void {
    const s = d.state;
    if (s.phase === PH_DRAFT) {
      if (d.mySeat === null || !d.isMyTurn) {
        tilebar.innerHTML = '<span>Waiting for the draft…</span>';
        return;
      }
      // A tile NAME on its own tells a first-time player nothing, and a title attribute needs a
      // mouse. The one moment they have to choose is the one moment the rule has to be on screen.
      tilebar.innerHTML = `<div class="draftlist">${s.offer
        .map((id, i) => {
          const p = powerOf(id)!;
          return `<button class="draft-card" data-draft="${i}"><b>${escapeHtml(p.name)}</b><span>${escapeHtml(p.text)}</span></button>`;
        })
        .join('')}</div>`;
      tilebar.querySelectorAll<HTMLElement>('[data-draft]').forEach((b) =>
        b.addEventListener('click', () => {
          const idx = Number(b.dataset.draft);
          if (submit(packDraft(idx))) ping('select');
        }),
      );
      return;
    }
    if (!mode.powers || d.mySeat === null) {
      tilebar.innerHTML = '';
      return;
    }
    const parts: string[] = [];
    for (const seat of [0, 1]) {
      const p = powerOf(s.power[seat]);
      if (!p) continue;
      const mine = seat === d.mySeat;
      const armable = mine && d.isMyTurn && s.phase === PH_PLAY && s.chg[seat] > 0 && ui.taps.length === 0;
      parts.push(
        `<button class="tile-chip${ui.armed !== 0 && mine ? ' armed' : ''}" ${armable ? '' : 'disabled'} ${mine ? 'data-arm="1"' : ''} title="${escapeHtml(p.text)}">${escapeHtml(mine ? p.name : `${d.names[seat]}: ${p.name}`)} · ${s.chg[seat]}</button>`,
      );
    }
    tilebar.innerHTML = parts.join('');
    tilebar.querySelector<HTMLElement>('[data-arm]')?.addEventListener('click', () => {
      ui.armed = ui.armed === 0 ? d.state.power[d.mySeat!] : 0;
      ui.taps = [];
      ping('blip');
      paint();
    });
  }

  function renderActions(): void {
    const canUndo = d.isMyTurn && (ui.taps.length > 0 || ui.sel !== null);
    actions.innerHTML = `${canUndo ? '<button class="btn" id="undo">Undo</button>' : ''}<button class="btn ghost" id="quit">Quit</button>`;
    actions.querySelector('#undo')?.addEventListener('click', () => {
      if (ui.taps.length > 0) ui.taps.pop();
      else ui.sel = null;
      ping('blip', 0.7);
      paint();
    });
    actions.querySelector('#quit')!.addEventListener('click', onQuit);
  }

  function renderClock(): void {
    if (d.clockMs <= 0 || isOver(d.state) || d.mySeat === null) {
      clockbar.hidden = true;
      return;
    }
    clockbar.hidden = !d.isMyTurn;
    const frac = Math.max(0, Math.min(1, d.turnLeft / d.clockMs));
    clockfill.style.width = `${frac * 100}%`;
    clockbar.classList.toggle('low', frac < 0.25);
  }

  function renderMoves(): void {
    const s = d.state;
    const rows: string[] = [];
    const probe = { turn: 0 };
    for (const ply of s.log) {
      if (((ply >>> 23) & 3) !== 0) continue;
      probe.turn++;
      const to = plyA(ply);
      const build = plyBuild(ply);
      rows.push(`<li>${probe.turn}. ${cellName(s, to)}${build === NONE ? ' — climbs!' : ` · builds ${cellName(s, build)}`}</li>`);
    }
    movelist.innerHTML = rows.slice(-24).join('');
  }

  function juice(events: PlyEvent[]): void {
    for (const e of events) {
      if (e.kind === 'move') ping('blip', 1);
      else if (e.kind === 'build') {
        ping('thud', 0.7 + (e.level ?? 1) * 0.18);
        if ((e.level ?? 0) >= 3) view.shake(3);
        if (e.cell !== undefined) view.burst(e.cell, PALETTE.slab3, 6);
      } else if (e.kind === 'dome') {
        ping('coin', 0.6);
        if (e.cell !== undefined) view.burst(e.cell, PALETTE.domeGleam, 10);
      } else if (e.kind === 'shove') {
        ping('thud', 1.3);
      } else if (e.kind === 'win') {
        ping('win');
        view.shake(6);
        if (e.cell !== undefined) view.burst(e.cell, PALETTE.gold, 28);
      } else if (e.kind === 'sealed') {
        ping('lose');
        view.shake(4);
      }
      if (!reducedMotion && 'vibrate' in navigator) {
        try {
          if (e.kind === 'win') navigator.vibrate([12, 30, 12]);
          else if (e.kind === 'build') navigator.vibrate(6);
        } catch {
          /* haptics are best-effort */
        }
      }
    }
  }

  function submit(ply: number): boolean {
    const s = d.state;
    const wasTurn = s.phase === PH_PLAY ? { from: s.pos[buildersOf(s, s.turn)[plyBuilder(ply)]], seat: s.turn } : null;
    const ok = d.submit(ply);
    if (!ok) return false;
    if (wasTurn) last = { from: wasTurn.from, to: plyA(ply), build: plyBuild(ply), seat: wasTurn.seat };
    ui = freshTurn();
    paint();
    maybeBot();
    return true;
  }
  activeSubmit = submit;

  function onCell(cell: number): void {
    if (!live || destroyed || !d.isMyTurn) return;
    const s = d.state;
    const legal = legalNow();

    if (s.phase === PH_PLACE) {
      const ply = legal.find((p) => plyA(p) === cell);
      if (ply === undefined) {
        view.nudge(cell);
        ping('thud', 0.6);
        return;
      }
      submit(ply);
      ping('select');
      return;
    }
    if (s.phase !== PH_PLAY) return;

    // Tapping one of your own builders picks it up (or puts it down again).
    const who = s.occ[cell];
    if (who >= 0 && seatOfBuilder(s, who) === s.turn && ui.taps.length === 0) {
      const k = buildersOf(s, s.turn).indexOf(who);
      ui.sel = ui.sel === k ? null : k;
      ui.taps = [];
      settle(legal, ui);
      ping('select');
      paint();
      return;
    }
    if (ui.sel === null) {
      view.nudge(cell);
      return;
    }

    const fields = FIELDS[ui.armed] ?? FIELDS[0];
    const next = fields[ui.taps.length];
    const cands = candidates(legal, ui);
    if (!next || !cands.some((p) => readField(p, next) === cell)) {
      view.nudge(cell);
      ping('thud', 0.6);
      return;
    }
    ui.taps.push(cell);
    const after = settle(legal, ui);
    if (ui.taps.length >= fields.length || after.length === 1) {
      const ply = after.find((p) => {
        for (let i = 0; i < ui.taps.length; i++) if (readField(p, fields[i]) !== ui.taps[i]) return false;
        return true;
      });
      if (ply !== undefined && (ui.taps.length >= fields.length || after.length === 1)) {
        submit(ply);
        return;
      }
    }
    ping('blip', 1.2);
    paint();
  }

  function maybeBot(): void {
    if (destroyed || !live || !d.isBotTurn || isOver(d.state)) return;
    if (botTimer !== null) return;
    d.botThinking = true;
    // setTimeout, never rAF: a backgrounded tab throttles rAF to nothing and the opponent's turn
    // would simply never arrive.
    botTimer = setTimeout(() => {
      botTimer = null;
      if (destroyed || isOver(d.state)) return;
      const ply = chooseAny(d.state, optsForTier(tierOf(myTier), d.state.seed + d.state.ply));
      d.botThinking = false;
      if (ply >= 0) submit(ply);
    }, 380);
  }

  // Measure the REAL elapsed time rather than trusting the interval. A backgrounded tab clamps
  // timers to about once a second, so feeding a hard-coded 250ms made every clock in the round run
  // at a quarter speed — the disconnect hold took eighty real seconds to fire instead of twenty,
  // which reads exactly like it never fires at all.
  let lastTick = Date.now();
  const clock = setInterval(() => {
    if (destroyed) return;
    const now = Date.now();
    const dt = Math.min(5000, Math.max(0, now - lastTick));
    lastTick = now;
    d.tick(dt);
  }, 250);

  function setPaused(on: boolean): void {
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (on) {
      if (ov) return;
      ov = document.createElement('div');
      ov.className = 'pause-ov';
      ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
        <button class="btn primary" id="resume">Resume</button>
        <button class="btn ghost" id="pquit">Quit</button></div>`;
      boardHost.appendChild(ov);
      ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
      ov.querySelector('#pquit')!.addEventListener('click', onQuit);
    } else {
      ov?.remove();
    }
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!boardHost.querySelector('.pause-ov')));

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'p' || e.key === 'P') setPaused(!boardHost.querySelector('.pause-ov'));
    else if (e.key === 'Escape' || e.key === 'u' || e.key === 'U') {
      if (ui.taps.length > 0) ui.taps.pop();
      else ui.sel = null;
      paint();
    }
  }
  window.addEventListener('keydown', onKey);

  d.deps.onChange = paint;
  d.deps.onEvents = (events) => juice(events);
  d.deps.onEnd = () => {
    setTimeout(() => {
      if (!destroyed) onOver();
    }, 900);
  };

  paint();

  return {
    destroy() {
      destroyed = true;
      clearInterval(clock);
      if (botTimer !== null) clearTimeout(botTimer);
      cd.cancel();
      view.destroy();
      window.removeEventListener('keydown', onKey);
      if (activeSubmit === submit) activeSubmit = null;
    },
  };
}

// ── solo ────────────────────────────────────────────────────────────────────────
let soloScreen: Screen | null = null;

function startSolo(): void {
  teardown();
  setPlaying(true);
  soloRound++;
  const mode = modeOf(myModeChoice);
  // Alternate which seat the player takes, so placement priority and tempo alternate too.
  const mySeat = soloRound % 2 === 1 ? 0 : 1;
  const names: [string, string] = mySeat === 0 ? [playerName, `${tierOf(myTier).name} bot`] : [`${tierOf(myTier).name} bot`, playerName];
  const d = new Duel({
    mode,
    seed: newSeed(),
    round: soloRound,
    names,
    mySeat,
    bot: optsForTier(tierOf(myTier)),
    deps: { send: () => {}, onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  duel = d;
  activeDuel = d;
  soloScreen = buildGameScreen(
    d,
    () => showMenu(),
    () => {
      if (d.state.winner >= 0) noteResult(mode.id, d.state.winner === mySeat);
      showResults(false);
    },
  );
  onCleanup(() => soloScreen?.destroy());
}

// ── multiplayer ─────────────────────────────────────────────────────────────────
let netSend: (m: WireMsg) => void = () => {};
let mvOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: Screen | null = null;
let beat: ReturnType<typeof setInterval> | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with a friend',
    subtitle: 'Start a room and send the link, or type a code you were given.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [0, 0];
  duel = null;
  activeDuel = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: () => repaintLobby(),
      onPeers: () => {
        repaintLobby();
        duel?.peerBack();
      },
      onPeerLeave: (id) => {
        const idx = roster.findIndex((p) => p.id === id);
        if (idx >= 0 && duel) duel.peerLeft(seatForRound(idx, roundNo));
        repaintLobby();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
      refreshResults();
    },
  });
  rounds = r;

  const send = n.channel<WireMsg>('mv', (data) => duel?.receive(data));
  netSend = (m) => send(m);
  mvOff = () => send.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 2,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  roundNo = info.round;
  const mode = modeOf(info.opts?.mode);
  const myIndex = roster.findIndex((p) => p.id === (net?.selfId ?? ''));
  const seated = info.seated && myIndex >= 0;
  const mySeat = seated ? seatForRound(myIndex, info.round) : null;
  const names: [string, string] = ['', ''];
  roster.slice(0, 2).forEach((p, i) => {
    const seat = seatForRound(i, info.round);
    // Two players CAN arrive with the same handle — the default one is random, and two tabs on one
    // device share the store outright. Identical names on a results screen make it unreadable, so
    // disambiguate with the seat's own name rather than showing "Builder 930" twice.
    names[seat] = p.name || SEAT_NAME[seat];
  });
  if (!names[0]) names[0] = SEAT_NAME[0];
  if (!names[1]) names[1] = SEAT_NAME[1];
  if (names[0] === names[1]) {
    names[0] = `${names[0]} (${SEAT_NAME[0]})`;
    names[1] = `${names[1]} (${SEAT_NAME[1]})`;
  }

  const d = new Duel({
    mode,
    seed: info.seed,
    round: info.round,
    names,
    mySeat,
    bot: null,
    clockMs: 60_000,
    deps: { send: (m) => netSend(m), onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  duel = d;
  activeDuel = d;
  setPlaying(true);

  if (beat !== null) clearInterval(beat);
  // The heartbeat is what makes a lost message a non-event: it re-sends the whole log every 2s.
  beat = setInterval(() => d.broadcast(), 2000);
  onCleanup(() => {
    if (beat !== null) clearInterval(beat);
    beat = null;
  });

  gameScreen = buildGameScreen(
    d,
    () => {
      // End this peer's round FIRST (phase playing → waiting) or the engine lobby paints nothing
      // for a peer still seated in a live round, and the player gets a blank screen.
      rounds?.finish();
      backToLobby();
    },
    () => showResults(true),
  );
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
  d.broadcast();
}

// ── results ─────────────────────────────────────────────────────────────────────
let resultsMounted = false;
let analysis: Analysis | null = null;
let verdict: Verdict | null = null;
let multi = false;

function showResults(isMulti: boolean): void {
  teardown();
  setPlaying(false);
  multi = isMulti;
  resultsMounted = true;
  const d = duel;
  if (!d) {
    showMenu();
    return;
  }
  if (d.state.winner >= 0 && d.state.reason !== 'nocontest') tally[d.state.winner]++;
  if (isMulti) rounds?.finish();

  content.innerHTML = `
    <section class="screen results-screen" id="res"></section>
    <div class="menu-actions" id="ract"></div>`;
  renderResults();

  const loser = d.state.winner >= 0 ? 1 - d.state.winner : -1;
  if (loser >= 0 && d.state.reason === 'climb' && d.fault === null) {
    analysis = analyse(d.mode.id, d.state.seed, d.state.log, loser, { name: d.names[loser], isSelf: d.mySeat === loser }, (v) => {
      verdict = v;
      const box = content.querySelector<HTMLElement>('#verdict');
      if (box) box.innerHTML = verdictHtml(v);
    });
  }
  onCleanup(() => {
    resultsMounted = false;
    analysis?.cancel();
    analysis = null;
    verdict = null;
  });
}

function renderResults(): void {
  const d = duel;
  if (!d || !resultsMounted) return;
  const box = content.querySelector<HTMLElement>('#res');
  const act = content.querySelector<HTMLElement>('#ract');
  if (!box || !act) return;

  box.innerHTML = resultsHtml({
    rows: d.results(),
    mode: d.mode,
    reason: d.reason,
    winner: d.state.winner,
    tally,
    round: multi ? roundNo : soloRound,
    fault: d.fault,
  });
  const iso = box.querySelector<HTMLCanvasElement>('#isoBoard');
  if (iso) renderIso(iso, d.state, d.state.reason === 'climb' && d.state.winner >= 0 ? d.state.pos[buildersOf(d.state, d.state.winner)[0]] : null);
  const vbox = box.querySelector<HTMLElement>('#verdict');
  if (vbox) vbox.innerHTML = verdictHtml(verdict);

  act.innerHTML = multi
    ? `<button class="btn primary" id="again">Play again</button>
       <p class="waiting-note" id="waiting" aria-live="polite"></p>
       <button class="btn" id="lobby">Back to lobby</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`
    : `<button class="btn primary" id="again">Play again</button>
       <button class="btn" id="share">Share</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`;

  act.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  act.querySelector('#menu')!.addEventListener('click', () => (multi ? void leaveRoom() : showMenu()));
  if (multi) {
    const again = act.querySelector<HTMLButtonElement>('#again')!;
    again.addEventListener('click', () => {
      rounds?.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the others…';
      refreshResults();
    });
    act.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
    refreshResults();
  } else {
    act.querySelector('#again')!.addEventListener('click', () => startSolo());
    act.querySelector('#share')!.addEventListener('click', () => void share());
  }
}

function refreshResults(): void {
  if (!resultsMounted || !multi) return;
  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (!waiting) return;
  const r = rounds?.state();
  if (r && r.startsInMs !== null) {
    waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
  } else if (r && r.votes.length) {
    waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
  } else {
    waiting.textContent = '';
  }
}

setInterval(() => refreshResults(), 400);

async function share(): Promise<void> {
  const d = duel;
  if (!d) return;
  const me = d.mySeat ?? 0;
  const won = d.state.winner === me;
  const text = `Skyhold · ${d.mode.name} — ${won ? 'held the sky' : 'lost the climb'} in ${d.results()[me].turns} turns\nhttps://skyhold.benrichardson.dev`;
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

function backToLobby(): void {
  teardown();
  duel = null;
  activeDuel = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  // Take the room out of the URL on the way out, or a reload — or reopening from a home-screen
  // icon — silently drops the player back into a room they deliberately left.
  clearRoomInUrl();
  lobbyMounted = false;
  duel = null;
  activeDuel = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    mvOff();
  } catch {
    /* the channel may already be gone */
  }
  mvOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// Exposed so the browser-verification pass can drive the REAL submit path synchronously — rAF is
// throttled in a hidden tab, and every phone check runs through this. Never read by gameplay.
(window as unknown as { __skyhold?: unknown }).__skyhold = {
  state(): unknown {
    const s = activeDuel?.state;
    if (!s) return null;
    return {
      mode: s.mode.id,
      phase: s.phase,
      turn: s.turn,
      ply: s.ply,
      over: isOver(s),
      winner: s.winner,
      reason: s.reason,
      lev: Array.from(s.lev),
      pos: Array.from(s.pos),
      mySeat: activeDuel?.mySeat ?? null,
      power: Array.from(s.power),
      chg: Array.from(s.chg),
      log: s.log.slice(),
    };
  },
  legal(): number[] {
    return activeDuel?.legal() ?? [];
  },
  play(ply: number): boolean {
    return activeSubmit ? activeSubmit(ply) : false;
  },
  /** Play one legal ply, chosen by the shipped search — enough to walk a whole game in a pane. */
  auto(): boolean {
    const d = activeDuel;
    if (!d || isOver(d.state)) return false;
    const ply = chooseAny(d.state, { depth: 1, timeMs: 0, nodes: 20_000 });
    return ply >= 0 && !!activeSubmit && activeSubmit(ply);
  },
  colours: SEAT_COLOUR,
  fmtTime,
};

boot();
